import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Login } from './pages/Login';
import { Catalog } from './pages/Catalog';
import { SuperAdminDashboard } from './pages/SuperAdminDashboard';
import { ERPDashboard } from './pages/ERPDashboard';
import { LandingPage } from './pages/LandingPage';
import { Toaster } from './components/ui/sonner';
import { ConfirmDialogHost } from './components/ConfirmDialog';
import GatewayErrorBanner from './components/GatewayErrorBanner';
import { setTenantHeader } from './lib/api';
import './App.css';

function AppRoutes() {
  const { tenant, isTenantSite, loading, error } = useTenant();

  React.useEffect(() => {
    if (tenant?.id) {
      setTenantHeader(tenant.id);
    }
  }, [tenant]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f0f23]" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (isTenantSite && error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f0f23]" data-testid="tenant-error">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold text-white mb-2">Loja não encontrada</h1>
          <p className="text-gray-400">Verifique o endereço e tente novamente.</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Landing Page */}
      <Route path="/" element={isTenantSite ? <Catalog /> : <LandingPage />} />

      {/* Super Admin */}
      <Route path="/superadmin/login" element={<Login />} />
      <Route path="/superadmin/*" element={
        <ProtectedRoute superAdminOnly>
          <SuperAdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/login" element={<Login />} />

      {/* ===== NEW URL PATTERN: /:slug/... ===== */}

      {/* Public catalog */}
      <Route path="/:slug/catalogo" element={<Catalog />} />

      {/* Tenant login */}
      <Route path="/:slug/login" element={<Login />} />

      {/* All authenticated tenant pages -> ERPDashboard handles routing internally */}
      <Route path="/:slug/dashboard" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/pdv" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/vendas" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/estoque" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/financeiro" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/clientes" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/fornecedores" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/caixa" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/orcamentos" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/relatorio" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/plano-contas" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/banco" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/formas-pagamento" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/fechamento" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/recibos" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/empresas" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/usuarios" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/perfis-acesso" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/config-empresa" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/configuracoes" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/meu-plano" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/produtos" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/categorias" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/pedidos" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/config-catalogo" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />
      <Route path="/:slug/*" element={<ProtectedRoute erpOnly><ERPDashboard /></ProtectedRoute>} />

      {/* Legacy routes - redirect support */}
      <Route path="/loja/:slug" element={<LegacyRedirect to="catalogo" />} />
      <Route path="/admin/:slug/login" element={<LegacyRedirect to="login" />} />
      <Route path="/admin/:slug/*" element={<LegacyRedirect to="dashboard" />} />
      <Route path="/erp/:slug/login" element={<LegacyRedirect to="login" />} />
      <Route path="/erp/:slug/*" element={<LegacyRedirect to="dashboard" />} />
    </Routes>
  );
}

// Legacy URL redirect component
const LegacyRedirect = ({ to }) => {
  const { slug } = require('react-router-dom').useParams();
  const { Navigate } = require('react-router-dom');
  return <Navigate to={`/${slug}/${to}`} replace />;
};

function App() {
  return (
    <TenantProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <AppRoutes />
            {/* iter156i: toast modernizado — top-center, fonte light/slim, bordas suaves e cores tematicas */}
            <Toaster
              position="top-center"
              richColors
              closeButton
              expand={false}
              visibleToasts={4}
              duration={4200}
              gap={10}
              offset={20}
              toastOptions={{
                style: {
                  minWidth: '380px',
                  maxWidth: '520px',
                  fontSize: '13.5px',
                  fontWeight: 300,
                  letterSpacing: '-0.01em',
                  padding: '14px 18px',
                },
              }}
            />
            <ConfirmDialogHost />
            {/* iter156as: banner amigavel para erros de gateway/Cloudflare (520/524/etc) */}
            <GatewayErrorBanner />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </TenantProvider>
  );
}

export default App;
